import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    signal,
    untracked,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IsActiveMatchOptions, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar/scrollbar.directive';
import { FuseUtilsService } from '@fuse/services/utils';
import { filter } from 'rxjs';

@Component({
    selector: 'fuse-vertical-navigation',
    templateUrl: './vertical.component.html',
    styleUrls: ['./vertical.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FuseScrollbarDirective,
        MatIconModule,
        MatTooltipModule,
        NgClass,
        NgTemplateOutlet,
        RouterLink,
        RouterLinkActive,
    ],
})
export class FuseVerticalNavigationComponent {
    private readonly router = inject(Router);
    private readonly fuseUtils = inject(FuseUtilsService);

    protected readonly exactMatchOptions = this.fuseUtils.exactMatchOptions;
    protected readonly subsetMatchOptions = this.fuseUtils.subsetMatchOptions;

    public readonly autoCollapse = input<boolean>(true);
    public readonly inner = input<boolean>(false);
    public readonly navigation = input<FuseNavigationItem[]>([]);

    protected navigationSignal = signal<FuseNavigationItem[]>([]);

    private readonly navigationEnd = toSignal(
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            takeUntilDestroyed(),
        ),
        { initialValue: null },
    );

    private lastNavEndId = -1;

    constructor() {
        effect(() => {
            const items = this.navigation();
            const navEnd = this.navigationEnd();
            untracked(() => {
                const cloned = this.cloneItems(items);
                if (!navEnd) {
                    this.navigationSignal.set(cloned);
                    return;
                }
                if (navEnd.id === this.lastNavEndId) {
                    this.navigationSignal.set(this.expandActiveRoute(cloned));
                } else {
                    this.lastNavEndId = navEnd.id;
                    this.navigationSignal.set(this.expandActiveRoute(this.collapseAll(cloned)));
                }
            });
        });
    }

    protected toggleItem(item: FuseNavigationItem, siblings: FuseNavigationItem[]): void {
        if (item.disabled) {
            return;
        }

        if (item.expanded) {
            item.expanded = false;
        } else {
            if (this.autoCollapse()) {
                for (const sibling of siblings) {
                    if (sibling !== item) {
                        sibling.expanded = false;
                    }
                }
            }
            item.expanded = true;
        }
    }

    private cloneItems(items: FuseNavigationItem[]): FuseNavigationItem[] {
        return items.map((item) => ({
            ...item,
            children: item.children ? this.cloneItems(item.children) : undefined,
        }));
    }

    private collapseAll(items: FuseNavigationItem[]): FuseNavigationItem[] {
        for (const item of items) {
            item.expanded = false;
            if (item.children?.length) {
                this.collapseAll(item.children);
            }
        }
        return items;
    }

    private expandActiveRoute(items: FuseNavigationItem[]): FuseNavigationItem[] {
        for (const item of items) {
            let hasActiveChild = false;

            if (item.children?.length) {
                item.children = this.expandActiveRoute(item.children);
                hasActiveChild = this.hasActiveDescendant(item.children);
            }

            if (hasActiveChild) {
                item.expanded = true;
            }
        }
        return items;
    }

    private hasActiveDescendant(items: FuseNavigationItem[]): boolean {
        for (const item of items) {
            if (item.link) {
                const options = this.isActiveOption(item.isActiveMatchOptions ?? { exact: item.exactMatch ?? false });
                if (this.router.isActive(item.link, options)) {
                    return true;
                }
            }
            if (item.children?.length && this.hasActiveDescendant(item.children)) {
                return true;
            }
        }
        return false;
    }

    protected isActiveOption(options: { exact: boolean } | IsActiveMatchOptions): IsActiveMatchOptions {
        const { exact } = options as { exact?: boolean };
        if (exact != null) {
            return exact ? this.exactMatchOptions : this.subsetMatchOptions;
        }
        return options as IsActiveMatchOptions;
    }
}
